import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { createVehicle } from '../services/vehicleService';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

// Form Bileşenleri
import VehicleBasicInfoForm from '../components/forms/VehicleBasicInfoForm';
import VehicleTechnicalForm from '../components/forms/VehicleTechnicalForm';
import VehicleDetailedTechnicalForm from '../components/forms/VehicleDetailedTechnicalForm';
import VehicleBodyConditionForm from '../components/forms/VehicleBodyConditionForm';
import VehiclePhotoUpload from '../components/forms/VehiclePhotoUpload';
import VehicleAdditionalForm from '../components/forms/VehicleAdditionalForm';

const AddVehiclePage = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [formData, setFormData] = useState({
        title: '',
        brand: '',
        model: '',
        bodyType: '',
        year: '',
        price: '',
        mileage: '',
        fuelType: 'Benzin',
        transmission: 'Manuel',
        color: '',
        description: '',
        features: '',
        location: {
            city: currentUser?.address?.city || '',
            district: currentUser?.address?.district || ''
        },
        // Yeni eklenecek detaylı alanlar
        generalFeatures: {
            acceleration: '',
            enginePower: '',
            maxSpeed: '',
        },
        engineDetails: {
            power: '',
            volume: '',
            torque: '',
        },
        fuelConsumption: {
            tankCapacity: '',
            cityConsumption: '',
            highwayConsumption: '',
        },
        dimensions: {
            tireSize: '',
            height: '',
            length: '',
            luggageCapacity: '',
            emptyWeight: '',
            loadedWeight: '',
            width: '',
        },
        bodyCondition: {
            frontBumper: '',
            rearBumper: '',
            roof: '',
            trunkLid: '',
            engineHood: '',
            leftFrontDoor: '',
            rightFrontDoor: '',
            leftRearDoor: '',
            rightRearDoor: '',
            leftFrontFender: '',
            rightFrontFender: '',
            leftRearFender: '',
            rightRearFender: '',
        }
    });

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Çoklu fotoğraf yükleme için state'ler
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [imageLoading, setImageLoading] = useState(false);
    const [imageError, setImageError] = useState('');
    const [debugInfo, setDebugInfo] = useState(null); // DEBUG için

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData({
                ...formData,
                [parent]: {
                    ...formData[parent],
                    [child]: value
                }
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    // Resim dosyaları seçildiğinde
    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Dosya türü ve boyut kontrolü
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        const invalidFiles = files.filter(file =>
            !validTypes.includes(file.type) || file.size > maxSize
        );

        if (invalidFiles.length > 0) {
            setImageError('Bazı dosyalar geçersiz format veya boyutta. Lütfen sadece JPG, JPEG veya PNG formatında, maksimum 5MB boyutunda dosyalar seçin.');
            return;
        }

        setImageError('');
        setImageFiles(prev => [...prev, ...files]);

        // Resim önizlemeleri
        files.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviews(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    // Önizleme resmini kaldır
    const handleRemoveImage = (index) => {
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
        setImageFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Tüm resimleri temizle
    const handleClearAllImages = () => {
        setImageFiles([]);
        setImagePreviews([]);
    };

    // Resimleri yükleme işlemi
    const uploadImages = async (vehicleId) => {
        if (!imageFiles.length || !vehicleId) return [];

        setImageLoading(true);
        setImageError('');
        setUploadProgress(0);

        try {
            // Tüm resimleri yüklemek için promises dizisi oluştur
            const uploadPromises = imageFiles.map(async (file, index) => {
                const formData = new FormData();
                formData.append('image', file);

                console.log(`Dosya ${index + 1} yükleme isteği gönderiliyor`);
                console.log('Authorization token:', localStorage.getItem('token') ? 'Mevcut' : 'Eksik');

                const response = await axios.post(
                    `/api/images/vehicles/${vehicleId}`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        onUploadProgress: (progressEvent) => {
                            // Her dosya için ilerleme hesapla
                            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                            console.log(`Dosya ${index + 1} yükleniyor: %${progress}`);

                            // Toplam ilerleme (basit yaklaşım)
                            setUploadProgress(
                                Math.round((index + (progress / 100)) / imageFiles.length * 100)
                            );
                        }
                    }
                );

                console.log('Yükleme yanıtı:', response.data);
                setDebugInfo(prev => ({
                    ...prev,
                    [`uploadResponse${index}`]: response.data
                }));

                // API yanıt yapısını kontrol et
                if (response.data && response.data.success && response.data.data && response.data.data.imageUrl) {
                    return response.data.data.imageUrl;
                }

                // Eğer beklenen yanıt yapısında değilse
                console.error('Beklenmeyen API yanıt yapısı:', response.data);
                return null;
            });

            // Tüm yüklemeleri bekle
            const uploadedImageUrls = await Promise.all(uploadPromises);

            // null değerleri filtrele
            const validUrls = uploadedImageUrls.filter(url => url !== null);

            setImageLoading(false);
            setUploadProgress(100);

            console.log('Tüm resimler yüklendi:', validUrls);
            setDebugInfo(prev => ({
                ...prev,
                allUploadedUrls: validUrls
            }));

            return validUrls;
        } catch (error) {
            console.error('Resim yükleme hatası:', error);
            setImageError('Resim yüklenirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
            setImageLoading(false);

            setDebugInfo(prev => ({
                ...prev,
                uploadError: error.message
            }));

            return [];
        }
    };

    // Araç verilerini getiren fonksiyon (yükleme sonrası)
    const fetchVehicleData = async (vehicleId) => {
        try {
            const response = await axios.get(
                `/api/vehicles/${vehicleId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            console.log('Araç verileri çekildi:', response.data);
            setDebugInfo(prev => ({
                ...prev,
                fetchedVehicleData: response.data
            }));

            return response.data;
        } catch (error) {
            console.error('Araç verilerini çekme hatası:', error);
            return null;
        }
    };

    // DEBUG bilgilerini göster
    const renderDebugInfo = () => {
        if (!debugInfo) return null;

        return (
            <div className="mt-4 p-3 border border-info rounded">
                <h5>DEBUG Bilgileri</h5>
                <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);
            setError('');
            setSuccessMessage('');

            // Sayısal değerlerin dönüşümü
            const vehicleData = {
                ...formData,
                year: Number(formData.year),
                price: Number(formData.price),
                mileage: Number(formData.mileage),
                features: formData.features.split(',').map(item => item.trim())
            };

            console.log('Araç verisi gönderiliyor:', vehicleData);
            setDebugInfo({
                vehicleDataSent: vehicleData
            });

            const result = await createVehicle(vehicleData);
            setDebugInfo(prev => ({
                ...prev,
                createVehicleResponse: result
            }));

            if (result.success && result.vehicle && result.vehicle._id) {
                const vehicleId = result.vehicle._id;
                console.log('Araç başarıyla oluşturuldu, ID:', vehicleId);
                setSuccessMessage('Araç başarıyla oluşturuldu!');

                // Eğer resim varsa yükle
                let uploadedImageUrls = [];
                if (imageFiles.length > 0) {
                    uploadedImageUrls = await uploadImages(vehicleId);

                    // Yükleme işlemi sonrası güncel araç verilerini çek
                    await fetchVehicleData(vehicleId);

                    // Yükleme başarısız olursa da detay sayfasına yönlendir, ama hata göster
                    if (!uploadedImageUrls || uploadedImageUrls.length === 0) {
                        setError('Araç oluşturuldu ancak fotoğraflar yüklenemedi.');
                        setTimeout(() => {
                            navigate(`/vehicles/${vehicleId}`);
                        }, 2000);
                        return;
                    }
                }

                // Başarılı olduktan sonra detay sayfasına yönlendir
                setTimeout(() => {
                    navigate(`/vehicles/${vehicleId}`);
                }, 2000);
            } else {
                setError(result.message || 'Araç eklenirken bir hata oluştu');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Sunucu hatası');
            console.error('Araç ekleme hatası:', error);

            setDebugInfo(prev => ({
                ...prev,
                submitError: error.message
            }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-5">
            <h1 className="mb-4">Yeni Araç Ekle</h1>

            {error && <Alert variant="danger">{error}</Alert>}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}

            {/* DEBUG bilgileri */}
            {renderDebugInfo()}

            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={8}>
                        {/* Modüler Form Bileşenleri */}
                        <VehicleBasicInfoForm data={formData} handleChange={handleChange} />
                        <VehicleTechnicalForm data={formData} handleChange={handleChange} />
                        <VehicleDetailedTechnicalForm data={formData} handleChange={handleChange} />
                        <VehicleBodyConditionForm data={formData} handleChange={handleChange} />
                        <VehicleAdditionalForm data={formData} handleChange={handleChange} />
                    </Col>

                    <Col md={4}>
                        {/* Fotoğraf Bölümü */}
                        <VehiclePhotoUpload
                            imagePreviews={imagePreviews}
                            handleImageChange={handleImageChange}
                            handleRemoveImage={handleRemoveImage}
                            handleClearAllImages={handleClearAllImages}
                            imageLoading={imageLoading}
                            imageError={imageError}
                            uploadProgress={uploadProgress}
                        />

                        {/* İşlem Butonları */}
                        <div className="d-grid gap-2">
                            <Button
                                variant="primary"
                                type="submit"
                                size="lg"
                                disabled={loading || imageLoading}
                            >
                                {loading || imageLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        {loading ? 'Araç Kaydediliyor...' : 'Fotoğraflar Yükleniyor...'}
                                    </>
                                ) : (
                                    'Aracı Ekle'
                                )}
                            </Button>

                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate('/vehicles')}
                                size="lg"
                            >
                                İptal
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Container>
    );
};

export default AddVehiclePage;